import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams, Link } from "react-router";
import { useForm } from "react-hook-form";
import Modal from "@/components/Common/Modal/Modal";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import emptyLogo from "@/images/empty_logo.png";
import loader from "@/images/loader.gif";
import { toast } from "@/components/common/Toast";
import { updateHubDetailsStepTwo } from "@/store/hubs/hubActions";
import tokens from "@/theme/tokens";
import Field from "@/components/FormInput/Field";
import {
  buttonRowSx,
  formSx,
  labelSx,
  pageIndexSx,
  questionSx,
  titleSx,
} from "./styles";

const VALID_IMAGE_TYPES = ["image/jpg", "image/jpeg", "image/png", "image/gif"];
const MAX_LOGO_BYTES = 5242880;

interface StepTwoValues {
  logo?: FileList | null;
  description: string;
}

const CreateHubStepTwo = () => {
  const { t } = useTranslation(["translation.Hub"]);
  const dispatch = useDispatch();
  const { h_id: hubId } = useParams();

  const isRequesting = useSelector((state: any) =>
    Boolean(state.metadata.isRequesting)
  );
  const [tempLogo, setTempLogo] = useState<string | null>(null);

  const { register, handleSubmit, watch, setValue } = useForm<StepTwoValues>({
    defaultValues: { logo: undefined, description: "" },
  });

  const logoList = watch("logo");
  const description = watch("description");
  const isFormEmpty = !(logoList && logoList.length) && !description;

  const triggerImageErrorPopup = () =>
    dispatch(toast.error(t("SupportedFormats")));
  const maxSizeErrorPopup = () => dispatch(toast.error(t("MaxPhotoSize")));

  const onLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reject the file: clear RHF state, the DOM file input (so the same file
    // can be re-selected and can't be submitted), and the preview.
    const rejectFile = (showError: () => void) => {
      setValue("logo", null);
      setTempLogo(null);
      e.target.value = "";
      showError();
    };

    if (file.size && file.size > MAX_LOGO_BYTES) {
      rejectFile(maxSizeErrorPopup);
      return;
    }

    if (!VALID_IMAGE_TYPES.includes(file.type)) {
      rejectFile(triggerImageErrorPopup);
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => setTempLogo(reader.result as string);
    reader.readAsDataURL(file);
  };

  const onSubmit = (values: StepTwoValues) =>
    dispatch(updateHubDetailsStepTwo(hubId, values));

  // Field forwards `inputRef` to its TextField, so register()'s ref goes
  // there and the rest of register()'s props spread onto the field.
  const { ref: descriptionRef, ...descriptionField } = register("description");

  return (
    // No dismiss path existed before this migration (no onClose was ever
    // passed to the raw Dialog) — dismissible=false preserves that exactly;
    // "Skip for now" below is the only way out, same as before.
    <Modal
      open
      onClose={() => {}}
      dismissible={false}
      slotProps={{ paper: { "aria-label": "Create Hub Modal" } }}
    >
      <Box
        component="form"
        encType="multipart/form-data"
        onSubmit={handleSubmit(onSubmit)}
        sx={formSx}
      >
        <Typography variant="h2" sx={titleSx}>
          {t("CreateHubStepTwoTitle")}
        </Typography>

        <Box sx={questionSx}>
          <Box component="span" sx={labelSx}>
            {t("Logo")}
          </Box>
          <Box
            sx={{
              display: "inline-block",
              "& input#logo": { display: "none" },
            }}
          >
            <Box
              component="img"
              alt="logo"
              src={tempLogo || emptyLogo}
              sx={{
                display: "block",
                width: 100,
                height: 100,
                border: "1px solid",
                borderColor: "grey.300",
              }}
            />
            <Box
              component="label"
              htmlFor="logo"
              sx={{
                display: "block",
                m: tokens.space.createHubLogoMargin,
                width: 100,
                px: 0,
                fontSize: tokens.fontSize.createHubLogoLabel,
                color: "primary.main",
                cursor: "pointer",
              }}
            >
              {t("SelectLogo")}
            </Box>
            <input
              id="logo"
              type="file"
              accept=".png, .jpg, .jpeg, .gif"
              {...register("logo", { onChange: onLogoChange })}
            />
          </Box>
        </Box>

        <Field
          fullWidth
          multiline
          rows={4}
          label={t("Description")}
          placeholder={t("DescriptionPlaceholder")}
          slotProps={{ htmlInput: { maxLength: 250 } }}
          {...descriptionField}
          inputRef={descriptionRef}
        />

        <Stack direction="row" sx={buttonRowSx}>
          <Box component="span" sx={pageIndexSx}>
            2/2
          </Box>
          <Button
            component={Link}
            to={`/hubs/${hubId}/activities`}
            variant="text"
            sx={{ color: "grey.600", mr: tokens.space.px15 }}
          >
            {t("SkipForNow")}
          </Button>
          {isRequesting ? (
            <Button
              type="button"
              disabled
              startIcon={<img src={loader} alt="" />}
            >
              {t("Working")}
            </Button>
          ) : (
            <Button type="submit" variant="contained" disabled={isFormEmpty}>
              {t("Done")}
            </Button>
          )}
        </Stack>
      </Box>
    </Modal>
  );
};

export default CreateHubStepTwo;
